import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Save, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ baseURL: API, withCredentials: true });
ax.interceptors.request.use((config) => {
  const token = localStorage.getItem("ng_admin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function Section({ title, children }) {
  return (
    <div className="bg-[#141414] border border-[#2E2E2E] p-5 mb-4">
      <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4" style={{ fontFamily: "'Clash Display', sans-serif" }}>{title}</h3>
      {children}
    </div>
  );
}

function RateField({ label, value, onChange, suffix }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <Label className="text-sm text-[#A3A3A3] flex-1">{label}</Label>
      <div className="flex items-center gap-1">
        {!suffix?.startsWith("%") && <span className="text-[#666] text-xs">$</span>}
        <Input type="number" step="0.01" value={value} onChange={(e) => onChange(parseFloat(e.target.value) || 0)} className="w-24 bg-[#1F1F1F] border-[#2E2E2E] text-white text-sm h-8 rounded-none text-right" />
        {suffix && <span className="text-[#666] text-xs ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

export default function PricingSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const fetchSettings = useCallback(async () => {
    try {
      const res = await ax.get("/admin/pricing-settings");
      setSettings(res.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
      else toast.error("Failed to load settings");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await ax.put("/admin/pricing-settings", settings);
      toast.success("Pricing settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const update = (path, value) => {
    setSettings((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let obj = copy;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return copy;
    });
  };

  if (loading || !settings) return <div className="min-h-screen bg-[#0A0A0A] pt-24 flex items-center justify-center text-[#A3A3A3]">Loading...</div>;

  const m = settings.materials || {};
  const hw = settings.hardware || {};
  const lab = settings.labour || {};
  const feat = settings.features || {};
  const entry = settings.entry_points || {};
  const margins = settings.margins || {};
  const mults = settings.multipliers || {};
  const other = settings.other_costs || {};

  return (
    <div data-testid="pricing-settings-page" className="min-h-screen bg-[#0A0A0A] pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Clash Display', sans-serif" }}>Pricing Settings</h1>
            <p className="text-sm text-[#A3A3A3] mt-1">Manage all rates, margins, and multipliers</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchSettings} className="p-2 border border-[#2E2E2E] text-[#A3A3A3] hover:border-[#FF5722] hover:text-[#FF5722] transition-colors"><RefreshCw size={16} /></button>
            <button data-testid="save-settings-btn" onClick={saveSettings} disabled={saving} className="flex items-center gap-2 bg-[#FF5722] text-white px-5 py-2 text-sm font-semibold hover:bg-[#E64A19] transition-colors disabled:opacity-50">
              <Save size={14} />{saving ? "Saving..." : "Save All"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Materials */}
          <Section title="Material Costs (per m&sup2;)">
            {Object.entries(m).map(([key, val]) => (
              <RateField key={key} label={val.name || key} value={val.cost_per_sqm} onChange={(v) => update(`materials.${key}.cost_per_sqm`, v)} suffix="/m&sup2;" />
            ))}
          </Section>

          {/* Hardware */}
          <Section title="Hardware Costs">
            <RateField label="Track (per linear m)" value={hw.track_per_lm} onChange={(v) => update("hardware.track_per_lm", v)} suffix="/lm" />
            <RateField label="Ceiling mount (each)" value={hw.ceiling_mount_each} onChange={(v) => update("hardware.ceiling_mount_each", v)} suffix="ea" />
            <RateField label="Wall mount (each)" value={hw.wall_mount_each} onChange={(v) => update("hardware.wall_mount_each", v)} suffix="ea" />
            <RateField label="Floor-ceiling (each)" value={hw.floor_to_ceiling_each} onChange={(v) => update("hardware.floor_to_ceiling_each", v)} suffix="ea" />
            <RateField label="Free-standing frame (/lm)" value={hw.freestanding_frame_per_lm} onChange={(v) => update("hardware.freestanding_frame_per_lm", v)} suffix="/lm" />
            <RateField label="I-beam clamp (each)" value={hw.ibeam_clamp_each} onChange={(v) => update("hardware.ibeam_clamp_each", v)} suffix="ea" />
          </Section>

          {/* Labour */}
          <Section title="Labour Rates">
            <RateField label="Fabrication (per hour)" value={lab.fabrication_rate_per_hour} onChange={(v) => update("labour.fabrication_rate_per_hour", v)} suffix="/hr" />
            <RateField label="Installation (per hour)" value={lab.installation_rate_per_hour} onChange={(v) => update("labour.installation_rate_per_hour", v)} suffix="/hr" />
            <RateField label="Engineering (per hour)" value={lab.engineering_rate_per_hour} onChange={(v) => update("labour.engineering_rate_per_hour", v)} suffix="/hr" />
            <RateField label="Fab output (m&sup2;/hr)" value={lab.fabrication_sqm_per_hour} onChange={(v) => update("labour.fabrication_sqm_per_hour", v)} suffix="m&sup2;/hr" />
            <RateField label="Install output (m&sup2;/hr)" value={lab.installation_sqm_per_hour} onChange={(v) => update("labour.installation_sqm_per_hour", v)} suffix="m&sup2;/hr" />
          </Section>

          {/* Margins */}
          <Section title="Margins (%)">
            <RateField label="Default margin" value={margins.default_margin_pct} onChange={(v) => update("margins.default_margin_pct", v)} suffix="%" />
            <RateField label="Minimum margin" value={margins.minimum_margin_pct} onChange={(v) => update("margins.minimum_margin_pct", v)} suffix="%" />
            <div className="border-t border-[#2E2E2E] mt-3 pt-3">
              <p className="text-xs text-[#666] uppercase tracking-wider mb-2">Product-Specific</p>
              {Object.entries(margins.product_margins || {}).map(([key, val]) => (
                <RateField key={key} label={key.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())} value={val} onChange={(v) => update(`margins.product_margins.${key}`, v)} suffix="%" />
              ))}
            </div>
          </Section>

          {/* Multipliers */}
          <Section title="Multipliers & Surcharges">
            <RateField label="Large job threshold" value={mults.large_job_threshold_sqm} onChange={(v) => update("multipliers.large_job_threshold_sqm", v)} suffix="m&sup2;" />
            <RateField label="Large job discount" value={mults.large_job_discount_pct} onChange={(v) => update("multipliers.large_job_discount_pct", v)} suffix="%" />
            <RateField label="Complex shape surcharge" value={mults.complex_shape_surcharge_pct} onChange={(v) => update("multipliers.complex_shape_surcharge_pct", v)} suffix="%" />
            <RateField label="Cyclone rating surcharge" value={mults.cyclone_rating_surcharge_pct} onChange={(v) => update("multipliers.cyclone_rating_surcharge_pct", v)} suffix="%" />
            <RateField label="Urgent job surcharge" value={mults.urgent_surcharge_pct} onChange={(v) => update("multipliers.urgent_surcharge_pct", v)} suffix="%" />
          </Section>

          {/* Other Costs */}
          <Section title="Other Costs">
            <RateField label="Freight base" value={other.freight_base} onChange={(v) => update("other_costs.freight_base", v)} />
            <RateField label="Freight per km" value={other.freight_per_km} onChange={(v) => update("other_costs.freight_per_km", v)} suffix="/km" />
            <RateField label="Waste allowance" value={other.waste_allowance_pct} onChange={(v) => update("other_costs.waste_allowance_pct", v)} suffix="%" />
            <RateField label="GST rate" value={other.gst_rate_pct} onChange={(v) => update("other_costs.gst_rate_pct", v)} suffix="%" />
            <RateField label="Minimum quote value" value={other.minimum_quote_value} onChange={(v) => update("other_costs.minimum_quote_value", v)} />
          </Section>

          {/* Features */}
          <Section title="Feature Costs">
            {Object.entries(feat).map(([key, val]) => (
              <div key={key} className="border-b border-[#1F1F1F] pb-2 mb-2 last:border-0">
                <p className="text-xs text-white font-semibold mb-1">{key.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</p>
                {val.cost_each !== undefined && <RateField label="Cost each" value={val.cost_each} onChange={(v) => update(`features.${key}.cost_each`, v)} suffix="ea" />}
                {val.cost_per_lm !== undefined && <RateField label="Cost per lm" value={val.cost_per_lm} onChange={(v) => update(`features.${key}.cost_per_lm`, v)} suffix="/lm" />}
                {val.cost_per_sqm !== undefined && <RateField label="Cost per m&sup2;" value={val.cost_per_sqm} onChange={(v) => update(`features.${key}.cost_per_sqm`, v)} suffix="/m&sup2;" />}
              </div>
            ))}
          </Section>

          {/* Entry Points */}
          <Section title="Entry Point Costs">
            {Object.entries(entry).map(([key, val]) => (
              <div key={key} className="border-b border-[#1F1F1F] pb-2 mb-2 last:border-0">
                <p className="text-xs text-white font-semibold mb-1">{key.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</p>
                <RateField label="Cost each" value={val.cost_each} onChange={(v) => update(`entry_points.${key}.cost_each`, v)} suffix="ea" />
                <RateField label="Labour hours" value={val.labour_hours} onChange={(v) => update(`entry_points.${key}.labour_hours`, v)} suffix="hrs" />
              </div>
            ))}
          </Section>
        </div>

        {/* Sticky save */}
        <div className="fixed bottom-6 right-6 z-40">
          <button onClick={saveSettings} disabled={saving} className="flex items-center gap-2 bg-[#FF5722] text-white px-6 py-3 shadow-lg shadow-[#FF5722]/20 font-semibold text-sm hover:bg-[#E64A19] transition-colors disabled:opacity-50">
            <Save size={16} />{saving ? "Saving..." : "Save All Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}
